import React, { useState } from 'react';
import styles from '../style/password-input.module.css';

const EyeIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" x2="23" y1="1" y2="23" />
  </svg>
);

/**
 * A password input with a show/hide visibility toggle button.
 *
 * @param {Object} props
 * @param {string} props.value - Current password value.
 * @param {function} props.onChange - Called when the input value changes.
 * @param {function} [props.onBlur] - Called when the input loses focus.
 * @param {string} props.placeholder - Placeholder text for the input.
 * @param {string} [props.inputClassName] - CSS class for the input element (from the host page's module).
 * @param {string} [props.wrapperClassName] - CSS class for the outer wrapper div.
 * @param {string} [props.errorClassName] - Additional CSS class applied when hasError is true.
 * @param {boolean} [props.hasError] - When true, applies errorClassName and sets aria-invalid.
 * @param {string} [props.autoComplete] - autoComplete attribute for the input.
 * @param {function} [props.onKeyDown] - Called on keydown events on the input.
 */
const PasswordInput = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
  inputClassName,
  wrapperClassName,
  errorClassName,
  hasError,
  autoComplete
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const inputClass =
    [inputClassName, hasError && errorClassName].filter(Boolean).join(' ') || undefined;
  const wrapperClass = [styles['password-wrapper'], wrapperClassName].filter(Boolean).join(' ');

  return (
    <div className={wrapperClass}>
      <input
        aria-invalid={!!hasError}
        autoComplete={autoComplete}
        className={inputClass}
        onBlur={onBlur}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        type={showPassword ? 'text' : 'password'}
        value={value}
      />
      <button
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        aria-pressed={showPassword}
        className={styles['password-toggle']}
        onClick={() => setShowPassword((prev) => !prev)}
        type="button"
      >
        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
};

export default PasswordInput;
